// Grounded pay insights. Everything here is derived deterministically from the
// employee's own payslip data + the engine's explanation strings + period diffs.
// It EXPLAINS; it never recomputes payroll. Numbers shown are server-vouched
// `facts`, kept separate from the prose so nothing can be fabricated.
import type { PayslipDetail, TrendSeries, ComponentType } from '../types';
import { computeCompare } from './compare';
import { CATEGORY_META } from './format';

export interface InsightFact {
  label: string;
  amount: number;
  tone?: 'pos' | 'neg' | 'neutral';
}

export interface Insight {
  id: string;
  question: string;
  prose: string[];
  facts: InsightFact[];
  citations: string[];
}

let seq = 0;
const id = () => `i${++seq}`;
const isDeduction = (t: ComponentType) => t === 'STATUTORY_TAX' || t === 'PRE_TAX_DEDUCTION' || t === 'POST_TAX_DEDUCTION';

export function whyDidPayChange(current: PayslipDetail, previous: PayslipDetail): Insight {
  const cmp = computeCompare(current, previous);
  const dir = cmp.net.deltaAmount > 0 ? 'higher' : cmp.net.deltaAmount < 0 ? 'lower' : 'unchanged';
  const prose = [
    `Your net pay was ${dir}${cmp.net.deltaPct != null && dir !== 'unchanged' ? ` by ${Math.abs(cmp.net.deltaPct).toFixed(1)}%` : ''} in ${current.periodLabel} versus ${previous.periodLabel}.`,
    ...cmp.narrative,
  ];
  return {
    id: id(),
    question: 'Why did my pay change?',
    prose,
    citations: [current.periodLabel, previous.periodLabel],
    facts: [
      { label: `Net · ${current.periodLabel}`, amount: cmp.net.current },
      { label: `Net · ${previous.periodLabel}`, amount: cmp.net.previous },
      { label: 'Change', amount: cmp.net.deltaAmount, tone: cmp.net.deltaAmount > 0 ? 'pos' : cmp.net.deltaAmount < 0 ? 'neg' : 'neutral' },
    ],
  };
}

export function topMover(current: PayslipDetail, previous: PayslipDetail): Insight | null {
  const cmp = computeCompare(current, previous);
  const mover = cmp.componentDeltas.find((c) => isDeduction(c.componentType));
  if (!mover) return null;
  // Pull the engine's own explanation for this line from the current payslip.
  let explanation: string | undefined;
  for (const g of current.categories) for (const it of g.items) if (it.code === mover.code) explanation = it.explanation;
  return {
    id: id(),
    question: `Why did ${mover.label} change?`,
    prose: [
      `${mover.label} ${mover.deltaAmount > 0 ? 'increased' : 'decreased'}${mover.deltaPct != null ? ` ${Math.abs(mover.deltaPct).toFixed(1)}%` : ''} from ${previous.periodLabel} to ${current.periodLabel}.`,
      ...(explanation ? [`This period it was: ${explanation}.`] : []),
      'Statutory amounts move with your gross pay and the country pack’s rates.',
    ],
    citations: [current.periodLabel, previous.periodLabel],
    facts: [
      { label: `${mover.label} · ${current.periodLabel}`, amount: mover.current, tone: 'neg' },
      { label: `${mover.label} · ${previous.periodLabel}`, amount: mover.previous, tone: 'neg' },
    ],
  };
}

export function explainDeductions(detail: PayslipDetail): Insight {
  const prose: string[] = [`Here’s how each deduction on your ${detail.periodLabel} statement was worked out:`];
  const facts: InsightFact[] = [];
  for (const g of detail.categories) {
    if (!isDeduction(g.type)) continue;
    for (const it of g.items) {
      if (it.amount === 0) continue;
      prose.push(`• ${it.label}${it.explanation ? ` — ${it.explanation}` : ''}.`);
      facts.push({ label: it.label, amount: it.amount, tone: 'neg' });
    }
  }
  if (facts.length === 0) prose.push('You had no deductions this period.');
  return { id: id(), question: 'Explain my deductions', prose, facts, citations: [detail.periodLabel] };
}

export function anomalies(series: TrendSeries): Insight {
  const pts = series.points;
  const prose: string[] = [];
  if (pts.length >= 2) {
    for (let i = 1; i < pts.length; i++) {
      const cur = pts[i];
      const prev = pts[i - 1];
      // Net swing
      if (prev.net !== 0) {
        const pct = ((cur.net - prev.net) / prev.net) * 100;
        if (Math.abs(pct) >= 15) prose.push(`${cur.periodLabel}: net pay ${pct > 0 ? 'jumped' : 'dropped'} ${Math.abs(pct).toFixed(0)}% vs the prior period.`);
      }
      // Appeared / disappeared component types
      const types = new Set<string>([...Object.keys(cur.byType), ...Object.keys(prev.byType)]);
      types.forEach((t) => {
        const a = (cur.byType as any)[t] ?? 0;
        const b = (prev.byType as any)[t] ?? 0;
        const meta = CATEGORY_META[t as ComponentType];
        if (b === 0 && a > 0) prose.push(`${cur.periodLabel}: ${meta?.label ?? t} started this period.`);
        else if (b > 0 && a === 0) prose.push(`${cur.periodLabel}: ${meta?.label ?? t} stopped this period.`);
      });
    }
  }
  if (prose.length === 0) prose.push('No unusual changes detected across your recent pay — it’s been steady.');
  return { id: id(), question: 'Any unusual changes?', prose: prose.slice(0, 6), facts: [], citations: [] };
}
